import { Subheading } from '@/src/tailwindui/heading';
import { DefinitionTemplate } from './hooks/useCreateEvent';
import { Input } from '@/src/tailwindui/input';
import { Text } from '@/src/tailwindui/text';
import { useEffect, useState, useRef } from 'react';
import { ColorPicker } from './ColorPicker';
import styled from 'styled-components';
import useAssets from './hooks/useAssets';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import LoadingIndicator from '@/src/Common/components/LoadingIndicator';
import { FeatureFlags } from '@/src/Features/lib/constants';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { Button } from '@/src/tailwindui/button';
import { FolderIcon } from '@heroicons/react/24/outline';
import Library from '@/src/Library/components/Library';

enum CustomizerType {
    color = 'color',
    imageUrl = 'imageUrl',
    text = 'text',
}

const DropZone = styled.div<{ isDragging: boolean }>`
    width: 200px;
    height: 200px;
    border: 2px dashed ${(props) => (props.isDragging ? '#2196f3' : '#ccc')};
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    background: ${(props) =>
        props.isDragging ? 'rgba(33, 150, 243, 0.1)' : 'transparent'};
    transition: all 0.3s ease;
`;

const ImagePreview = styled.img<{ isHovering: boolean }>`
    max-width: 200px;
    max-height: 200px;
    object-fit: contain;
    background-color: #ffffff;
    background-image: linear-gradient(45deg, #dbdbdb 25%, transparent 25%),
        linear-gradient(-45deg, #dbdbdb 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #dbdbdb 75%),
        linear-gradient(-45deg, transparent 75%, #dbdbdb 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    transform: scale(0.9);
    transition: transform 0.3s ease;
`;

const ImageDimensions = styled.p`
    font-size: 0.875rem;
    color: #666;
    margin-top: 8px;
`;

export default function ExperienceCustomizer({
    experience,
    onValueChange,
    values,
}: {
    experience: DefinitionTemplate | null;
    onValueChange: (key: string, value: any) => void;
    values: Record<string, any>;
}) {
    const [templateValues, setTemplateValues] = useState<any[]>([]);
    const [showLibrary, setShowLibrary] = useState(false);
    const [libraryCallback, setLibraryCallback] = useState<
        ((value: string) => void) | null
    >(null);
    useEffect(() => {
        if (experience) {
            const parsedExperience = JSON.parse(
                experience.definitionJsonString,
            );
            setTemplateValues(parsedExperience.templateValues);
        }
    }, [experience]);

    if (templateValues.length === 0) {
        return null;
    }
    return (
        <>
            {Object.keys(templateValues).map((key: any) => {
                const value = templateValues[key];
                if (value.type === CustomizerType.color) {
                    return (
                        <ColorCustomizer
                            defaultValue={values[key] || value.defaultValue}
                            name={value.name}
                            description={value.description}
                            valueKey={key}
                            onValueChange={onValueChange}
                            key={`${key}`}
                        />
                    );
                }

                if (value.type === CustomizerType.imageUrl) {
                    return (
                        <div className="flex items-center gap-2">
                            <ImageCustomizer
                                defaultValue={values[key] || value.defaultValue}
                                name={value.name}
                                description={value.description}
                                valueKey={key}
                                onValueChange={onValueChange}
                                key={`${key}-${values[key]}`}
                            />
                            <FeatureFlag flag={FeatureFlags.PE_LIBRARY}>
                                <div>
                                    <Button
                                        className="text-sm text-[#000000] light-button-no-transparency dark:text-white plex-sans flex flex-row items-center gap-x-2  p-2 cursor-pointer"
                                        onClick={() => {
                                            const callback = (
                                                string: string,
                                            ) => {
                                                onValueChange(key, string);
                                            };
                                            setLibraryCallback(() => callback);
                                            setShowLibrary(true);
                                        }}
                                    >
                                        <FolderIcon className="w-5 h-5 mr-2" />
                                        Library
                                    </Button>
                                </div>
                            </FeatureFlag>
                        </div>
                    );
                }

                if (value.type === CustomizerType.text) {
                    return (
                        <TextCustomizer
                            defaultValue={values[key] || value.defaultValue}
                            name={value.name}
                            description={value.description}
                            valueKey={key}
                            onValueChange={onValueChange}
                            key={`${key}-${values[key]}`}
                        />
                    );
                }
                return null;
            })}
            {showLibrary && (
                <div
                    className="fixed inset-0 bg-black/50 bg-opacity-80 flex justify-center items-center"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowLibrary(false);
                        setLibraryCallback(null);
                    }}
                >
                    <Library
                        onImageSelected={(value: string) => {
                            console.log('value', value);
                            if (libraryCallback) {
                                libraryCallback(value);
                            }
                            setLibraryCallback(null);
                        }}
                        onClose={(e: any) => {
                            e.stopPropagation();
                            setShowLibrary(false);
                            setLibraryCallback(null);
                        }}
                    />
                </div>
            )}
        </>
    );
}

function ColorCustomizer({
    defaultValue,
    name,
    description,
    valueKey,
    onValueChange,
}: {
    defaultValue: string;
    name: string;
    description: string;
    valueKey: string;
    onValueChange: (key: string, value: any) => void;
}) {
    const [color, setColor] = useState(defaultValue);

    useEffect(() => {
        onValueChange(valueKey, color);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [color]);

    return (
        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 mb-10 mt-6">
            <div className="space-y-1">
                <Subheading>{name}</Subheading>
                <Text>{description}</Text>
            </div>
            <div className="flex items-center gap-2">
                <ColorPicker value={color} onChange={setColor} />
                <span className="text-sm text-black-500 uppercase dark:text-white">
                    {color}
                </span>
            </div>
        </section>
    );
}

function ImageCustomizer({
    defaultValue,
    name,
    description,
    valueKey,
    onValueChange,
}: {
    defaultValue: string;
    name: string;
    description: string;
    valueKey: string;
    onValueChange: (key: string, value: any) => void;
}) {
    const { uploadAsset } = useAssets();
    const { orgId } = useUserOrg();
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [imageDimensions, setImageDimensions] = useState<{
        width: number;
        height: number;
    } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [value, setValue] = useState(defaultValue);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            handleImageUpload(file);
        }
    };

    const handleImageUpload = async (file: File) => {
        setIsUploading(true);
        const result = await uploadAsset({
            // @ts-ignore
            asset: file,
            name: file.name,
            folder: 'customizer',
            orgId: orgId.toString(),
        });

        const img = new Image();
        img.onload = async () => {
            setImageDimensions({
                width: img.width,
                height: img.height,
            });
            setIsUploading(false);
            setValue(result.url);
        };
        img.src = result.url;
    };

    useEffect(() => {
        if (value) {
            const img = new Image();
            img.onload = () => {
                setImageDimensions({
                    width: img.width,
                    height: img.height,
                });
            };
            img.src = value;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    useEffect(() => {
        onValueChange(valueKey, value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            handleImageUpload(file);
        }
    };

    return (
        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 mb-10 mt-6">
            <div className="space-y-1">
                <Subheading>{name}</Subheading>
                <Text>{description}</Text>
            </div>
            <div className="flex items-center gap-2">
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleFileInput}
                />
                <DropZone
                    isDragging={isDragging}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleClick}
                >
                    {isUploading ? (
                        <LoadingIndicator />
                    ) : value ? (
                        <ImagePreview
                            src={value}
                            isHovering={isHovering}
                            onMouseEnter={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                        />
                    ) : (
                        <>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <p className="text-sm text-gray-500 dark:text-white text-center">
                                Drop image here or click to upload
                            </p>
                        </>
                    )}
                </DropZone>
                {imageDimensions && (
                    <ImageDimensions>
                        Dimensions:
                        <br />
                        {imageDimensions.width}×{imageDimensions.height}
                    </ImageDimensions>
                )}
            </div>
        </section>
    );
}

function TextCustomizer({
    defaultValue,
    name,
    description,
    valueKey,
    onValueChange,
}: {
    defaultValue: string;
    name: string;
    description: string;
    valueKey: string;
    onValueChange: (key: string, value: any) => void;
}) {
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        onValueChange(valueKey, value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 mb-10 mt-6">
            <div className="space-y-1">
                <Subheading>{name}</Subheading>
                <Text>{description}</Text>
            </div>
            <div className="flex items-center gap-2 ">
                <Input
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="monospace-font"
                />
            </div>
        </section>
    );
}
